import { Component, ViewEncapsulation, input, output } from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { SpinnerComponent } from '../../spinner/spinner.component';

export interface FileDropzoneConfig {
  acceptedTypes?: string[];
  maxFileSize?: number; // in bytes
  multiple?: boolean;
  disabled?: boolean;
}

export interface FileUploadProgress {
  loaded: number;
  total: number;
  percentage: number;
}

@Component({
  selector: 'app-file-dropzone',
  templateUrl: './file-dropzone.component.html',
  styleUrls: ['./file-dropzone.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [DecimalPipe, SpinnerComponent]
})
export class FileDropzoneComponent {
  config = input<FileDropzoneConfig>({
    acceptedTypes: ['.step', '.stp'],
    maxFileSize: 50 * 1024 * 1024, // 50MB default
    multiple: false,
    disabled: false
  });

  title = input<string>('Upload Files');
  subtitle = input<string>('Supported formats');
  changeLabel = input<string>('Change Files');
  description = input<string>('Drag and drop files here or click to select');
  customUploadMessage = input<string | null>(null);
  isUploading = input<boolean>(false);
  uploadProgress = input<FileUploadProgress | null>(null);
  errorMessage = input<string | null>(null);
  selectedFiles = input<File[]>([]);

  filesSelected = output<File[]>();
  filesDropped = output<File[]>();
  uploadCanceled = output<void>();

  isDragOver = false;
  private fileInput: HTMLInputElement | null = null;

  /** Mutable copy of selected files for internal manipulation */
  private _selectedFiles: File[] = [];

  get acceptedTypesString(): string {
    return this.config().acceptedTypes?.join(', ') || '';
  }

  get isDisabled(): boolean {
    return this.config().disabled || this.isUploading();
  }

  get hasFiles(): boolean {
    return this._selectedFiles.length > 0;
  }

  get progressPercentage(): number {
    return this.uploadProgress()?.percentage || 0;
  }

  get currentFiles(): File[] {
    return this._selectedFiles.length > 0 ? this._selectedFiles : this.selectedFiles();
  }

  onDragOver(event: DragEvent): void {
    if (this.isDisabled) return;

    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    if (this.isDisabled) return;

    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  onDrop(event: DragEvent): void {
    if (this.isDisabled) return;

    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;

    const files = Array.from(event.dataTransfer?.files || []);
    this.handleFiles(files);
  }

  onFileInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files || []);
    this.handleFiles(files);
  }

  onDropzoneClick(): void {
    if (this.isDisabled) return;

    if (!this.fileInput) {
      this.fileInput = document.createElement('input');
      this.fileInput.type = 'file';
      this.fileInput.accept = this.acceptedTypesString;
      this.fileInput.multiple = this.config().multiple || false;
      this.fileInput.addEventListener('change', (e) => this.onFileInputChange(e));
    }

    this.fileInput.click();
  }

  private handleFiles(files: File[]): void {
    const validFiles = this.validateFiles(files);

    if (validFiles.length > 0) {
      this._selectedFiles = this.config().multiple ? validFiles : [validFiles[0]];
      this.filesSelected.emit(this._selectedFiles);
      this.filesDropped.emit(this._selectedFiles);
    }
  }

  private validateFiles(files: File[]): File[] {
    const validFiles: File[] = [];
    const cfg = this.config();

    for (const file of files) {
      // Check file type
      if (cfg.acceptedTypes && cfg.acceptedTypes.length > 0) {
        const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
        if (!cfg.acceptedTypes.includes(fileExtension)) {
          continue;
        }
      }

      // Check file size
      if (cfg.maxFileSize && file.size > cfg.maxFileSize) {
        continue;
      }

      validFiles.push(file);
    }

    return validFiles;
  }

  removeFile(index: number): void {
    if (this.isDisabled) return;

    this._selectedFiles.splice(index, 1);
    this.filesSelected.emit(this._selectedFiles);
  }

  clearFiles(): void {
    if (this.isDisabled) return;

    this._selectedFiles = [];
    this.filesSelected.emit(this._selectedFiles);
  }

  cancelUpload(): void {
    this.uploadCanceled.emit();
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }
}
